/**
 * Discount status + human-readable value, ported from the source app's
 * `discount.utils.js`. Adapted to Cinch field names: unit grants reference
 * `price_metric` (the source's `unit_economic_id`), and an archived discount is
 * never selectable regardless of its date window.
 */

import type { CatalogDiscount, PriceMetric } from "./api";

export interface DiscountStatus {
  label: "Active" | "Expired" | "Scheduled" | "Archived";
  active: boolean;
}

export function discountStatus(d: CatalogDiscount): DiscountStatus {
  if (d.is_archived) return { label: "Archived", active: false };
  const now = Date.now();
  if (d.end_date && new Date(d.end_date).getTime() < now)
    return { label: "Expired", active: false };
  if (d.start_date && new Date(d.start_date).getTime() > now)
    return { label: "Scheduled", active: false };
  return { label: "Active", active: true };
}

function metricName(priceMetrics: PriceMetric[], id: string | null): string {
  return priceMetrics.find((m) => m.id === id)?.name ?? "units";
}

function grantLine(g: NonNullable<CatalogDiscount["unit_grants"]>[number], priceMetrics: PriceMetric[]): string {
  const name = metricName(priceMetrics, g.price_metric);
  const suffix = g.recurrence === "one_time" ? " (one-time)" : "/mo";
  return `${Number(g.quantity).toLocaleString()} ${name}${suffix}`;
}

/** Short label describing what the discount does (e.g. "10%", "$50.00",
 *  "3 months free", "500 emails/mo free"). */
export function displayValue(d: CatalogDiscount, priceMetrics: PriceMetric[] = []): string {
  if (d.type === "percentage") return `${d.value}%`;
  if (d.type === "fixed") return `$${Number(d.value ?? 0).toFixed(2)}`;
  if (d.type === "tiered") return `${d.value}% (${d.min_quantity || 0}+ units)`;
  if (d.type === "time_based") {
    const n = d.duration_periods || 0;
    const unit = `${d.period_unit || "month"}${n === 1 ? "" : "s"}`;
    if (Number(d.value) === 100) return `${n} ${unit} free`;
    return `${d.value}% off for ${n} ${unit}`;
  }
  if (d.type === "quantity_based") {
    const grants = d.unit_grants || [];
    if (grants.length === 0) return "No units granted";
    return `${grants.map((g) => grantLine(g, priceMetrics)).join(" + ")} free`;
  }
  return "—";
}
